import { Check, CircleDot, Fuel, Lightbulb, Plane, Stethoscope, TriangleAlert } from 'lucide-react';
import { useIntl } from '@/core/i18n/use-intl';
import type { StatusColor, StatusOption, StatusValue } from '@/shared/schema';
import { cn } from '@/shared/utils';
import { getCurrentStatus } from '@/shared/utils/status';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  fuel: Fuel,
  helideck: Plane,
  warning: TriangleAlert,
  check: Check,
  lightning: Lightbulb,
  medical: Stethoscope,
};

// Color classes for status indicators
const colorClasses: Record<StatusColor, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const colorTextClasses: Record<StatusColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
};

interface StatusIndicatorProps {
  /** The status field value containing mode, ranges, and default status */
  statusValue: StatusValue | undefined | null;
  /** Status options configuration (for labels) */
  statusOptions?: StatusOption[];
  /** Icon name to display */
  icon?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show as a simple dot indicator */
  dotOnly?: boolean;
  /** Whether to show the label next to the icon/dot */
  showLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Optional reference date for status calculation (for testing) */
  referenceDate?: Date;
}

/**
 * Status indicator component that displays the current status visually.
 * Can be displayed as a colored dot, with an icon, or with a label.
 */
export function StatusIndicator({
  statusValue,
  statusOptions,
  icon,
  size = 'md',
  dotOnly = false,
  showLabel,
  className,
  referenceDate,
}: StatusIndicatorProps) {
  const { locale } = useIntl();

  const currentStatus = getCurrentStatus(statusValue, referenceDate);
  const IconComponent = icon ? iconMap[icon] : null;

  // Get label for current status
  const getLabel = (color: StatusColor): string => {
    if (!statusOptions) return color;
    const option = statusOptions.find((opt) => opt.color === color);
    if (!option) return color;
    return locale === 'de-DE' ? option.labelDe : option.labelEn || option.labelDe;
  };

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  const iconSizeClasses = {
    sm: 'size-3',
    md: 'size-4',
    lg: 'size-5',
  };

  const label = getLabel(currentStatus);
  const shouldShowLabel = showLabel ?? (!IconComponent && !!statusOptions);

  // Simple dot indicator (for tab headers)
  if (dotOnly) {
    if (IconComponent) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn('inline-flex items-center justify-center', className)}
              data-testid={`status-indicator-icon-${currentStatus}`}
            >
              <IconComponent
                className={cn(iconSizeClasses[size], colorTextClasses[currentStatus])}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-block rounded-full flex-shrink-0',
              sizeClasses[size],
              colorClasses[currentStatus],
              className
            )}
            data-testid={`status-indicator-dot-${currentStatus}`}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Icon with status color
  if (IconComponent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn('inline-flex items-center gap-1.5', className)}
            data-testid={`status-indicator-icon-${currentStatus}`}
          >
            <IconComponent className={cn(iconSizeClasses[size], colorTextClasses[currentStatus])} />
            {shouldShowLabel && <span className="text-sm">{label}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Default: dot with optional label
  return (
    <span
      className={cn('inline-flex items-center gap-1.5', className)}
      data-testid={`status-indicator-${currentStatus}`}
    >
      <span
        className={cn('inline-block rounded-full', sizeClasses[size], colorClasses[currentStatus])}
      />
      {shouldShowLabel && <span className="text-sm">{label}</span>}
    </span>
  );
}

/**
 * Tab status indicator - simplified version for use in tab headers.
 * Shows a small colored dot next to the tab name.
 */
interface TabStatusIndicatorProps {
  statusValue: StatusValue | undefined | null;
  statusOptions?: StatusOption[];
  icon?: string;
  className?: string;
  referenceDate?: Date;
}

export function TabStatusIndicator({
  statusValue,
  statusOptions,
  icon,
  className,
  referenceDate,
}: TabStatusIndicatorProps) {
  return (
    <StatusIndicator
      statusValue={statusValue}
      statusOptions={statusOptions}
      icon={icon}
      size="sm"
      dotOnly
      className={className}
      referenceDate={referenceDate}
    />
  );
}

/**
 * Compact status badge for use in lists or cards.
 */
interface StatusBadgeProps {
  statusValue: StatusValue | undefined | null;
  statusOptions?: StatusOption[];
  icon?: string;
  className?: string;
  referenceDate?: Date;
}

export function StatusBadge({
  statusValue,
  statusOptions,
  icon,
  className,
  referenceDate,
}: StatusBadgeProps) {
  const { locale } = useIntl();
  const currentStatus = getCurrentStatus(statusValue, referenceDate);
  const IconComponent = icon ? iconMap[icon] : CircleDot;

  const getLabel = (color: StatusColor): string => {
    if (!statusOptions) return '';
    const option = statusOptions.find((opt) => opt.color === color);
    if (!option) return '';
    return locale === 'de-DE' ? option.labelDe : option.labelEn || option.labelDe;
  };

  const label = getLabel(currentStatus);

  const badgeColorClasses: Record<StatusColor, string> = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        badgeColorClasses[currentStatus],
        className
      )}
      data-testid={`status-badge-${currentStatus}`}
    >
      <IconComponent className="size-3" />
      {label && <span>{label}</span>}
    </span>
  );
}
